import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { SubmitButton, Overlay } from "@components";
import { Box, TextField, FormControlLabel, Checkbox } from "@mui/material";
import type { FormAction } from "features/users/types";
import type { ValidationRules } from "features/languages/types";
import type { ValidationTemplate } from "features/languages/types/validation-templates";
import { ValidationRulesList } from "features/languages/components/ValidationRulesList";
import { validationTemplatesService } from "features/languages/services/validation-templates-services";
import { useNotification } from "@hooks/useNotifications";
import { RULE_REPLACE_MAP } from "features/languages/utils/constants";
import { formRowStyle, formStyle, checkboxValueStyle } from "./styles";

const validationSchema = yup.object({
  name: yup.string().required("Name is required!"),
});

const rulesToString = (arr: string[]) => arr?.map(item => `"${RULE_REPLACE_MAP[item] ?? item}"`)?.join(", ");

export const ValidationTemplateForm = ({
  action,
  validationTemplate,
  validationRules,
}: {
  action?: FormAction;
  validationTemplate: ValidationTemplate;
  validationRules: ValidationRules;
}) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const [isUpdating, setIsUpdating] = useState(false);
  const isCreate = action === "create";

  const formik = useFormik({
    initialValues: {
      id: validationTemplate.id ?? null,
      isDefault: validationTemplate.isDefault,
      name: validationTemplate.name,
      rules: validationTemplate.rules,
      additionalRules: {
        isStartEndValidationEnabled: validationTemplate.additionalRules.isStartEndValidationEnabled,
        isSpecialCharValidationEnabled: validationTemplate.additionalRules.isSpecialCharValidationEnabled,
        isSameNumberAfterMacro: validationTemplate.additionalRules.isSameNumberAfterMacro,
      },
    },
    validationSchema,
    onSubmit: async formValue => {
      const controller = new AbortController();
      setIsUpdating(true);

      try {
        const response = await validationTemplatesService.saveValidationTemplate(
          {
            ...formValue,
          },
          controller,
        );

        if (response?.id) {
          showSuccess(isCreate ? "Template created." : " Template updated.");
          navigate(-1);
        }
      } catch (error) {
        showError("Template update failed. Please try again or contact support.");
        console.error(error);
      }

      setIsUpdating(false);
    },
  });

  return (
    <Box sx={formStyle}>
      <Overlay isVisible={isUpdating} />
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={formRowStyle}>
            <TextField
              required
              id="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Box>
          <Box sx={{ width: 200 }}>
            <FormControlLabel
              disabled={validationTemplate.isDefault}
              control={<Checkbox id="isDefault" checked={formik.values.isDefault} onChange={formik.handleChange} />}
              label='Is "default" template'
            />
          </Box>
        </Box>

        <Box sx={formRowStyle}>
          <FormControlLabel
            control={
              <Checkbox
                id="additionalRules.isStartEndValidationEnabled"
                checked={formik.values.additionalRules.isStartEndValidationEnabled}
                onChange={formik.handleChange}
              />
            }
            label={
              <Box>
                <Box>Check that there are no characters at the beginning of the translation:</Box>
                <Box
                  sx={checkboxValueStyle}
                  dangerouslySetInnerHTML={{
                    __html: rulesToString(validationRules.additionalRules.startDisallowedCharacters),
                  }}
                />
                <Box sx={{ mt: 1 }}>Check that there are no characters at the end of the translation:</Box>
                <Box
                  sx={checkboxValueStyle}
                  dangerouslySetInnerHTML={{
                    __html: rulesToString(validationRules.additionalRules.endDisallowedCharacters),
                  }}
                />
              </Box>
            }
          />
        </Box>

        <Box sx={formRowStyle}>
          <FormControlLabel
            control={
              <Checkbox
                id="additionalRules.isSpecialCharValidationEnabled"
                checked={formik.values.additionalRules.isSpecialCharValidationEnabled}
                onChange={formik.handleChange}
              />
            }
            label={
              <Box>
                <Box dangerouslySetInnerHTML={{ __html: 'Check that there are ONLY characters BEFORE "<" and "{":' }} />
                <Box
                  sx={checkboxValueStyle}
                  dangerouslySetInnerHTML={{
                    __html: rulesToString(validationRules.additionalRules.beforeSpecialCharactersDisallowed),
                  }}
                />
                <Box
                  sx={{ mt: 1 }}
                  dangerouslySetInnerHTML={{ __html: 'Check that there are ONLY characters AFTER ">" and "}":' }}
                />
                <Box
                  sx={checkboxValueStyle}
                  dangerouslySetInnerHTML={{
                    __html: rulesToString(validationRules.additionalRules.afterSpecialCharactersDisallowed),
                  }}
                />
              </Box>
            }
          />
        </Box>

        <Box sx={formRowStyle}>
          <FormControlLabel
            control={
              <Checkbox
                id="additionalRules.isSameNumberAfterMacro"
                checked={formik.values.additionalRules.isSameNumberAfterMacro}
                onChange={formik.handleChange}
              />
            }
            label={<Box>Check that the numbers following the macro are the same.</Box>}
          />
        </Box>

        <Box sx={formRowStyle}>
          <ValidationRulesList validationRules={validationRules} formik={formik} />
        </Box>

        <SubmitButton variant="contained" isProcessing={isUpdating} onClick={() => {}}>
          {isCreate ? "Create" : "Save"}
        </SubmitButton>
      </Box>
    </Box>
  );
};
